import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineTicket } from "react-icons/hi";
import useMethodAccount from "../../../MyAccount/hooks/useMethodAccount";
import EASkeletonLoader from "../../../shared/EASkeletonLoader";
import SinglePass from "./SinglePass";
import ListExpandControls from "../ListExpandControls";
import useParentMessage from "../../../../../utils/hooks/useParentMessage";
import { filterTicketsByStatus, getTicketStatusSummary } from "../../../../utils/TicketPassHelper";
import { Ticket } from "../../../../models/Ticket";
import { isAfter, isEqual, startOfDay } from "date-fns";

const ITEMS_PER_PAGE = 8;

const PassesList: React.FC = () => {
  const { t } = useTranslation();
  const { scrollToTop } = useParentMessage();
  const { passes, tickets, getLimited, getSelect, ticketsLoading } = useMethodAccount();
  const [displayedActiveCount, setDisplayedActiveCount] = useState(ITEMS_PER_PAGE);
  const [displayedExpiredCount, setDisplayedExpiredCount] = useState(ITEMS_PER_PAGE);

  const handleRefreshAllTickets = () => {
    getLimited();
    getSelect();
  };

  const handleActiveCollapseAll = () => {
    setDisplayedActiveCount(ITEMS_PER_PAGE);
    scrollToTop();
  };

  const handleExpiredCollapseAll = () => {
    setDisplayedExpiredCount(ITEMS_PER_PAGE);
    scrollToTop();
  };

  const isTodayOrLater = (dateNumber: number): boolean => {
    const today = startOfDay(new Date());
    const inputDate = startOfDay(new Date(dateNumber));

    return isEqual(inputDate, today) || isAfter(inputDate, today);
  };

  // Bilety na wybrany dzień (tak jak w starej sekcji FutureTickets)
  const futureDayTickets = useMemo(
    () =>
      (tickets || []).filter(
        (ticket) => ticket.passtype === 2 && ticket.ticket === true && isTodayOrLater(ticket.validto),
      ),
    [tickets],
  );

  // Karnety z GET_USER_SELECTS_URL (np. SELECT, OPEN/limited, które nie pojawiają się w GET_USER_PASSES_URL)
  const selectPassesFromSelects = useMemo(() => (tickets || []).filter((ticket) => ticket.ticket === false), [tickets]);

  // Połącz karnety (GET_USER_PASSES_URL) z biletami na wybrany dzień (GET_USER_SELECTS_URL)
  const mergedPasses = useMemo(() => {
    const map = new Map<string, Ticket>();

    (passes || []).forEach((p) => map.set(p.trhId, p));
    futureDayTickets.forEach((t) => map.set(t.trhId, t));
    selectPassesFromSelects.forEach((p) => map.set(p.trhId, p));

    return Array.from(map.values());
  }, [passes, futureDayTickets, selectPassesFromSelects]);

  const { active, expired } = useMemo(() => filterTicketsByStatus(mergedPasses), [mergedPasses]);

  useEffect(() => {
    handleRefreshAllTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sortuje bilety: najpierw te które wymagają akcji (status error/warning)
   */
  const sortPassesByStatus = (passes: Ticket[]): Ticket[] => {
    return [...passes].sort((a, b) => {
      const statusA = getTicketStatusSummary(a);
      const statusB = getTicketStatusSummary(b);

      const priorityA = statusA.color === "error" ? 0 : statusA.color === "warning" ? 1 : 2;
      const priorityB = statusB.color === "error" ? 0 : statusB.color === "warning" ? 1 : 2;

      return priorityA - priorityB;
    });
  };

  const sortedActive = useMemo(() => sortPassesByStatus(active), [active]);
  const sortedExpired = useMemo(() => sortPassesByStatus(expired), [expired]);

  if (ticketsLoading) {
    return <EASkeletonLoader height="120px" />;
  }

  return (
    <div className="list-container">
      {/* SEKCJA: AKTYWNE BILETY/KARNETY */}

      {/* <EAChip label={t("aktywne")}  variant="success" /> */}
      <span className="text-size-body-large text-color-primary text-icon-align-center">
        <HiOutlineTicket /> <b>{t("aktywne")}</b>
      </span>
      <div className="list-wrapper">
        {sortedActive.length > 0 ? (
          <>
            {sortedActive.slice(0, displayedActiveCount).map((ticket) => (
              <SinglePass key={ticket.trhId} pass={ticket} onRefreshTickets={handleRefreshAllTickets} />
            ))}
            {(displayedActiveCount < sortedActive.length || displayedActiveCount > ITEMS_PER_PAGE) && (
              <ListExpandControls
                displayedCount={displayedActiveCount}
                totalCount={sortedActive.length}
                setDisplayedCount={setDisplayedActiveCount}
                itemsPerPage={ITEMS_PER_PAGE}
                showMoreLabelKey="pokaz_wiecej"
                onCollapseAllAndScrollTop={handleActiveCollapseAll}
              />
            )}
          </>
        ) : (
          <span className="text-size-body-small text-color-dark-gray text-icon-align-center">{t("brak_biletow")}</span>
        )}
      </div>

      {/* SEKCJA: WYGASŁE BILETY/KARNETY */}
      <span className="text-size-body-large text-color-dark-gray text-icon-align-center">
        <HiOutlineTicket /> <b>{t("Wygasłe")}</b>
      </span>
      {sortedExpired.length > 0 ? (
        <div className="list-wrapper">
          {sortedExpired.slice(0, displayedExpiredCount).map((ticket) => (
            <SinglePass key={ticket.trhId} pass={ticket} onRefreshTickets={handleRefreshAllTickets} />
          ))}
          {(displayedExpiredCount < sortedExpired.length || displayedExpiredCount > ITEMS_PER_PAGE) && (
            <ListExpandControls
              displayedCount={displayedExpiredCount}
              totalCount={sortedExpired.length}
              setDisplayedCount={setDisplayedExpiredCount}
              itemsPerPage={ITEMS_PER_PAGE}
              showMoreLabelKey="pokaz_starsze"
              onCollapseAllAndScrollTop={handleExpiredCollapseAll}
            />
          )}
        </div>
      ) : (
        <span className="text-size-body-small text-color-dark-gray text-icon-align-center">{t("brak_biletow")}</span>
      )}
    </div>
  );
};;

export default PassesList;
